import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  message,
  Space,
  Tooltip,
  Descriptions,
  Spin,
  Switch,
  Checkbox,
  Dropdown,
  Typography,
  Alert,
  Collapse,
} from 'antd';
import type { MenuProps } from 'antd';
import { DownOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  shipmentApi,
  preDeclarationApi,
  routeApi,
  transportModeApi,
  channelApi,
  customerApi,
  cartonApi,
  type ShipmentDTO,
  type RouteDTO,
  type TransportModeDTO,
  type ChannelDTO,
  type CustomerDTO,
  type CustomerAddressDTO,
  type CustomerAddressSummaryDTO,
  type CartonDTO,
  type SubmitDepotContextDTO,
  type SubmitToDepotRequest,
  type SubmitToDepotResultDTO,
  type ShipmentInvoiceDTO,
  type TrackingDisplayDTO,
  type CheckAgentOrderResultDTO,
  type AgentCargoItemDTO,
  type SyncAgentDataResultDTO,
  type SyncTrackingDataResultDTO,
} from '../api/kfc';
import CartonDetailDrawer from '../components/CartonDetailDrawer';
import TrackingDisplayPanel from '../components/TrackingDisplayPanel';
import ResponsiveDrawer from '../components/ResponsiveDrawer';
import AgentMeasurementTable from '../components/AgentMeasurementTable';
import {
  buildStructuredClientAddr,
  formatClientAddr,
  isIntelinkQ5Identifier,
  missingQ5ConsigneeFields,
} from '../utils/clientAddr';
import { clientTablePagination, serverTablePagination } from '../utils/tablePagination';
import { parseAgentMeasurementJson } from '../utils/agentMeasurementDisplay';
import { highlightText } from '../utils/highlightText';

const REMARK_MAX_LENGTH = 500;
const CHANNEL_SHIPMENT_ID_MAX_LENGTH = 64;
const SERVICE_TYPE_OPTIONS = [
  { label: '专线 (D2D)', value: 'D2D' },
  { label: '港到港 (P2P)', value: 'P2P' },
];
const serviceTypeLabel = (v?: string) => {
  if (v === 'P2P') return '港到港';
  if (v === 'D2D' || !v) return '专线';
  return v;
};
const channelDisplay = (s: { channelCode?: string; channelName?: string; channelId?: string }) => {
  if (s.channelCode || s.channelName) {
    return [s.channelCode, s.channelName].filter(Boolean).join(' - ');
  }
  return s.channelId || '-';
};
const statusOptions = [
  { label: '预申报', value: 'PRE_DECLARATION' },
  { label: '已交代理仓', value: 'SUBMITTED_TO_DEPOT' },
  { label: '交货确认中', value: 'HANDOVER_CONFIRMING' },
  { label: '准备运输', value: 'CONFIRMED_WAIT_DEPARTURE' },
  { label: '运输中', value: 'IN_TRANSIT' },
  { label: '待派送', value: 'OUT_FOR_DELIVERY' },
  { label: '已签收', value: 'DELIVERED' },
];

const statusLabel: Record<string, string> = {
  PRE_DECLARATION: '预申报',
  PENDING_HANDOVER: '预申报',
  SUBMITTED_TO_DEPOT: '已交代理仓',
  HANDOVER_CONFIRMING: '交货确认中',
  CONFIRMED_WAIT_DEPARTURE: '准备运输',
  IN_TRANSIT: '运输中',
  OUT_FOR_DELIVERY: '待派送',
  DELIVERED: '已签收',
};

const cartonStateLabel: Record<string, string> = {
  preparing: '待收货',
  inKFYWarehouse: '抵达凯飞越仓库',
  inAgentWarehouse: '抵达代理仓库(出发地)',
  readyForShipping: '准备运输',
  onTheWay: '在途运输',
  arrivedDstPort: '抵达目的港',
  recevied: '已签收',
};

const agentInboundStatusLabel: Record<number, string> = {
  0: '新增',
  1: '已下单',
  2: '收货中',
  3: '已收货',
  4: '待确认',
  5: '已确认',
  6: '已删除',
  7: '调拨中',
  8: '扣货',
  9: '退货',
};
const agentOutboundStatusLabel: Record<number, string> = {
  0: '待出库',
  1: '出库中',
  2: '已出库',
};

type ListRow = { type: 'shipment'; key: string; s: ShipmentDTO };

const ShipmentList: React.FC = () => {
  const [rows, setRows] = useState<ListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [clientCode, setClientCode] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [filterShipmentIdInput, setFilterShipmentIdInput] = useState('');
  const [appliedShipmentIdKeyword, setAppliedShipmentIdKeyword] = useState('');
  const [weightModal, setWeightModal] = useState<ShipmentDTO | null>(null);
  const [submitDepotModal, setSubmitDepotModal] = useState<ShipmentDTO | null>(null);
  const [submitDepotContext, setSubmitDepotContext] = useState<SubmitDepotContextDTO | null>(null);
  const [submitDepotContextLoading, setSubmitDepotContextLoading] = useState(false);
  const [submitDepotForm] = Form.useForm<{
    depotId: string;
    hubInCode?: string;
    huolalaOrderNo?: string;
    photoUrls?: string;
    weig?: number;
    forceBind?: boolean;
  }>();
  const [submitDepotSubmitting, setSubmitDepotSubmitting] = useState(false);
  const [syncConfirmModal, setSyncConfirmModal] = useState<{ shipment: ShipmentDTO; check: CheckAgentOrderResultDTO } | null>(null);
  const [syncConfirmSubmitting, setSyncConfirmSubmitting] = useState(false);
  const [syncCargoSelectedKeys, setSyncCargoSelectedKeys] = useState<React.Key[]>([]);
  const [checkAgentMiss, setCheckAgentMiss] = useState<CheckAgentOrderResultDTO | null>(null);
  const [updateRouteModal, setUpdateRouteModal] = useState<ShipmentDTO | null>(null);
  const [updateRouteForm] = Form.useForm<{
    serviceType: string;
    transportMode: string;
    channelId?: string;
    routeCode?: string;
  }>();
  const [updateRouteSubmitting, setUpdateRouteSubmitting] = useState(false);
  const [remarkModal, setRemarkModal] = useState<ShipmentDTO | null>(null);
  const [remarkForm] = Form.useForm<{ remark?: string }>();
  const [remarkSubmitting, setRemarkSubmitting] = useState(false);
  const [channelShipmentModal, setChannelShipmentModal] = useState<ShipmentDTO | null>(null);
  const [channelShipmentForm] = Form.useForm<{ channelShipmentId?: string }>();
  const [channelShipmentSubmitting, setChannelShipmentSubmitting] = useState(false);
  const [manualShipmentId, setManualShipmentId] = useState(false);
  const [checkAgentOrderLoading, setCheckAgentOrderLoading] = useState(false);
  const [manualInboundVerifying, setManualInboundVerifying] = useState(false);
  const [manualAgentInboundNo, setManualAgentInboundNo] = useState('');
  const [submitInvoiceModal, setSubmitInvoiceModal] = useState<ShipmentDTO | null>(null);
  const [syncAgentDataModal, setSyncAgentDataModal] = useState<ShipmentDTO | null>(null);
  const [syncTrackingDataModal, setSyncTrackingDataModal] = useState<ShipmentDTO | null>(null);
  const [syncAgentDataSubmitting, setSyncAgentDataSubmitting] = useState(false);
  const [syncTrackingDataSubmitting, setSyncTrackingDataSubmitting] = useState(false);
  const [syncAgentDataFromDrawerLoading, setSyncAgentDataFromDrawerLoading] = useState(false);
  const [resolveCargoSyncLoading, setResolveCargoSyncLoading] = useState(false);
  const [syncTrackingDataFromDrawerLoading, setSyncTrackingDataFromDrawerLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [bindModalOpen, setBindModalOpen] = useState(false);
  const [bindingShipment, setBindingShipment] = useState<ShipmentDTO | null>(null);
  const [cartonsForBind, setCartonsForBind] = useState<CartonDTO[]>([]);
  const [selectedCartonIds, setSelectedCartonIds] = useState<string[]>([]);
  const [bindCartonLoading, setBindCartonLoading] = useState(false);
  /** 运单详情滑窗 */
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailShipment, setDetailShipment] = useState<ShipmentDTO | null>(null);
  const [detailShipmentLoading, setDetailShipmentLoading] = useState(false);
  const [detailCartonsLoading, setDetailCartonsLoading] = useState(false);
  const [detailInvoicesLoading, setDetailInvoicesLoading] = useState(false);
  const detailReqSeqRef = useRef(0);
  const [detailCartons, setDetailCartons] = useState<CartonDTO[]>([]);
  const [detailInvoices, setDetailInvoices] = useState<ShipmentInvoiceDTO[]>([]);
  const [detailTrackingDisplay, setDetailTrackingDisplay] = useState<TrackingDisplayDTO | null>(null);
  const [detailTrackingDisplayLoading, setDetailTrackingDisplayLoading] = useState(false);
  /** 详情侧滑内视图：运单 | 货物（单层，禁止嵌套 Drawer） */
  const [detailView, setDetailView] = useState<'shipment' | 'carton'>('shipment');
  const [cartonDetailId, setCartonDetailId] = useState<string | undefined>(undefined);
  const [routes, setRoutes] = useState<RouteDTO[]>([]);
  const [transportModes, setTransportModes] = useState<TransportModeDTO[]>([]);
  const [channels, setChannels] = useState<ChannelDTO[]>([]);
  const [customers, setCustomers] = useState<CustomerDTO[]>([]);
  const [addresses, setAddresses] = useState<CustomerAddressSummaryDTO[]>([]);
  const [addressDetail, setAddressDetail] = useState<CustomerAddressDTO | null>(null);
  const [addressDetailLoading, setAddressDetailLoading] = useState(false);
  const [weightForm] = Form.useForm();
  const [createForm] = Form.useForm();

  const load = () => {
    setLoading(true);
    shipmentApi
      .list({
        page,
        size: pageSize,
        clientCode,
        status: status || undefined,
        shipmentIdKeyword: appliedShipmentIdKeyword || undefined,
      })
      .then((res) => {
        const body = (res.data as any)?.data ?? res.data;
        const content = body?.content ?? [];
        const totalElements = body?.totalElements ?? 0;
        const shipRows: ListRow[] = (content as ShipmentDTO[]).map((s) => ({ type: 'shipment', key: `s-${s.shipmentId}`, s }));
        setRows(shipRows);
        setTotal(totalElements);
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [page, pageSize, clientCode, status, appliedShipmentIdKeyword]);

  const applyFilters = () => {
    setAppliedShipmentIdKeyword(filterShipmentIdInput.trim());
    setPage(0);
  };

  const resetShipmentIdFilter = () => {
    setFilterShipmentIdInput('');
    setAppliedShipmentIdKeyword('');
    setPage(0);
  };

  useEffect(() => {
    routeApi.list(true).then((res) => setRoutes(res.data || [])).catch(() => setRoutes([]));
    transportModeApi.list(true).then((res) => setTransportModes(res.data || [])).catch(() => setTransportModes([]));
    channelApi.list().then((res) => setChannels(res.data || [])).catch(() => setChannels([]));
    customerApi.list().then((res) => setCustomers(res.data || [])).catch(() => setCustomers([]));
  }, []);

  const onCustomerChange = (customerCode: string) => {
    createForm.setFieldValue('clientAddr', undefined);
    setAddressDetail(null);
    if (!customerCode) {
      setAddresses([]);
      return;
    }
    customerApi
      .listAddressSummaries(customerCode)
      .then((res) => setAddresses(res.data || []))
      .catch(() => setAddresses([]));
  };

  const loadAddressDetail = (addressId?: string) => {
    if (!addressId) {
      message.warning('请先选择地址');
      return;
    }
    setAddressDetailLoading(true);
    customerApi
      .getAddressDetail(addressId)
      .then((res) => setAddressDetail(res.data))
      .catch(() => {
        setAddressDetail(null);
        message.error('加载地址详情失败');
      })
      .finally(() => setAddressDetailLoading(false));
  };

  const resolveAddressForSubmit = async (addressId: string): Promise<CustomerAddressDTO | null> => {
    if (addressDetail?.id === addressId) return addressDetail;
    try {
      const res = await customerApi.getAddressDetail(addressId);
      setAddressDetail(res.data);
      return res.data;
    } catch {
      message.error('加载地址详情失败');
      return null;
    }
  };

  const onFinishCreate = async (values: Record<string, unknown>) => {
    const addressId = values.clientAddr as string;
    const selectedAddr = await resolveAddressForSubmit(addressId);
    if (!selectedAddr) return;
    const clientAddrStr = buildStructuredClientAddr(selectedAddr);
    if (!selectedAddr.phone?.trim()) {
      message.warning('所选客户地址缺少电话，哲盟等代理交仓需要收件电话，请先完善地址簿');
      return;
    }
    preDeclarationApi
      .create({
        clientCode: values.clientCode as string,
        clientAddr: clientAddrStr,
        serviceType: values.serviceType as string,
        transportMode: values.transportMode as string,
        channelId: (values.channelId as string) || undefined,
        routeCode: (values.routeCode as string) || undefined,
        shipmentId: manualShipmentId ? ((values.shipmentId as string)?.trim() || undefined) : undefined,
        expectedShipDate: values.expectedShipDate ? dayjs(values.expectedShipDate as dayjs.Dayjs).format('YYYY-MM-DD') : undefined,
        cargoDescJson: (values.cargoDescJson as string)?.trim() || '',
        customsDeclJson: undefined,
        remark: (values.remark as string)?.trim() || undefined,
      })
      .then((res) => {
        const sid = res.data?.shipmentId || res.data?.preDeclarationCode;
        if (sid) {
          Modal.success({
            title: '预申报创建成功',
            content: (
              <div>
                <div>
                  运单号：
                  <Typography.Text copyable={{ text: sid }} strong>
                    {sid}
                  </Typography.Text>
                </div>
                <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>
                  可复制后告知代理客服在代理系统建单，交仓时将自动查重同步
                </div>
              </div>
            ),
          });
        } else {
          message.success('预申报创建成功');
        }
        createForm.resetFields();
        setManualShipmentId(false);
        setAddressDetail(null);
        setCreateModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '创建失败'));
  };

  const openBindModal = (s: ShipmentDTO) => {
    setBindingShipment(s);
    setSelectedCartonIds([]);
    setBindModalOpen(true);
    const clientCode = s.clientCode;
    const customer = customers.find((c) => c.customerCode === clientCode);
    cartonApi
      .list({
        belongClientId: [clientCode, customer?.id].filter(Boolean) as string[],
        forBind: true,
        bindShipmentId: s.shipmentId,
      })
      .then((res) => {
        setCartonsForBind(cartonApi.unwrapList(res.data));
      })
      .catch(() => setCartonsForBind([]));
  };

  const onBindCartons = () => {
    if (!bindingShipment || selectedCartonIds.length === 0) {
      message.warning('请勾选要绑定的货物');
      return;
    }
    setBindCartonLoading(true);
    const shipmentId = bindingShipment.shipmentId;
    cartonApi
      .bindShipment({ cartonIds: selectedCartonIds, shipmentId })
      .then(() => {
        message.success('绑定成功');
        setSelectedCartonIds([]);
        setBindModalOpen(false);
        setBindingShipment(null);
        load();
        if (detailDrawerOpen && detailShipment?.shipmentId === shipmentId) {
          cartonApi.list({ shipmentId }).then((res) => {
            setDetailCartons(cartonApi.unwrapList(res.data));
          });
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '绑定失败'))
      .finally(() => setBindCartonLoading(false));
  };

  const openDetailDrawer = (s: ShipmentDTO) => {
    const reqId = ++detailReqSeqRef.current;
    setDetailShipment(null);
    setDetailCartons([]);
    setDetailInvoices([]);
    setDetailTrackingDisplay(null);
    setDetailView('shipment');
    setCartonDetailId(undefined);
    setDetailDrawerOpen(true);
    setDetailShipmentLoading(true);
    setDetailCartonsLoading(true);
    setDetailInvoicesLoading(true);

    shipmentApi
      .get(s.shipmentId)
      .then((shipRes) => {
        if (reqId !== detailReqSeqRef.current) return;
        setDetailShipment(shipRes.data);
      })
      .catch(() => {
        if (reqId !== detailReqSeqRef.current) return;
        message.warning('加载运单信息失败');
      })
      .finally(() => {
        if (reqId === detailReqSeqRef.current) setDetailShipmentLoading(false);
      });

    cartonApi
      .list({ shipmentId: s.shipmentId })
      .then((cartonRes) => {
        if (reqId !== detailReqSeqRef.current) return;
        setDetailCartons(cartonApi.unwrapList(cartonRes.data));
      })
      .catch(() => {
        if (reqId !== detailReqSeqRef.current) return;
        message.warning('加载绑定货物失败');
      })
      .finally(() => {
        if (reqId === detailReqSeqRef.current) setDetailCartonsLoading(false);
      });

    shipmentApi
      .listInvoices(s.shipmentId)
      .then((invRes) => {
        if (reqId !== detailReqSeqRef.current) return;
        setDetailInvoices(Array.isArray(invRes.data) ? invRes.data : []);
      })
      .catch(() => {
        if (reqId !== detailReqSeqRef.current) return;
        message.warning('加载发票列表失败');
      })
      .finally(() => {
        if (reqId === detailReqSeqRef.current) setDetailInvoicesLoading(false);
      });
  };

  const closeDetailDrawer = () => {
    detailReqSeqRef.current += 1;
    setDetailDrawerOpen(false);
    setDetailView('shipment');
    setCartonDetailId(undefined);
  };

  const openCartonInDetail = (cartonId: string) => {
    setCartonDetailId(cartonId);
    setDetailView('carton');
  };

  const backToShipmentDetail = () => {
    setDetailView('shipment');
    setCartonDetailId(undefined);
  };

  /** 当详情抽屉打开且运单有代理入库单号时，拉取轨迹展示数据 */
  useEffect(() => {
    if (!detailDrawerOpen || !detailShipment?.shipmentId || !detailShipment?.agentInboundNo?.trim()) {
      if (!detailDrawerOpen) setDetailTrackingDisplay(null);
      setDetailTrackingDisplayLoading(false);
      return;
    }
    setDetailTrackingDisplayLoading(true);
    shipmentApi
      .getTrackingDisplay(detailShipment.shipmentId)
      .then((res) => setDetailTrackingDisplay(res.data ?? null))
      .catch(() => setDetailTrackingDisplay(null))
      .finally(() => setDetailTrackingDisplayLoading(false));
  }, [detailDrawerOpen, detailShipment?.shipmentId, detailShipment?.agentInboundNo]);

  const refreshDetailInvoices = () => {
    if (!detailShipment?.shipmentId) return;
    shipmentApi.listInvoices(detailShipment.shipmentId).then((res) => {
      setDetailInvoices(Array.isArray(res.data) ? res.data : []);
    });
  };

  const refreshDetailCartons = () => {
    if (!detailShipment?.shipmentId) return;
    setDetailCartonsLoading(true);
    cartonApi
      .list({ shipmentId: detailShipment.shipmentId })
      .then((res) => {
        setDetailCartons(cartonApi.unwrapList(res.data));
      })
      .finally(() => setDetailCartonsLoading(false));
  };

  const onWeightOk = () => {
    const s = weightModal!;
    weightForm.validateFields().then((values) => {
      shipmentApi
        .weightConfirm({ shipmentId: s.shipmentId, confirmedWeight: values.confirmedWeight, accept: true })
        .then(() => {
          message.success('重量已确认');
          setWeightModal(null);
          weightForm.resetFields();
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '操作失败'));
    });
  };

  const openCreateDepotFlow = (s: ShipmentDTO, checkMiss?: CheckAgentOrderResultDTO | null) => {
    setSubmitDepotModal(s);
    setSubmitDepotContext(null);
    setCheckAgentMiss(checkMiss || null);
    submitDepotForm.resetFields();
    setSubmitDepotContextLoading(true);
    shipmentApi
      .getSubmitDepotContext(s.shipmentId)
      .then((res) => {
        setSubmitDepotContext(res.data);
        if (res.data?.suggestedWeig != null) {
          submitDepotForm.setFieldsValue({ weig: res.data.suggestedWeig });
        }
        const firstHub = (res.data?.serviceChannels || []).find((c) => c.serviceCode)?.serviceCode;
        if (firstHub) {
          submitDepotForm.setFieldsValue({ hubInCode: firstHub });
        }
      })
      .catch((e) => {
        message.error(e.response?.data?.message || '获取代理信息失败');
        setSubmitDepotModal(null);
        setCheckAgentMiss(null);
      })
      .finally(() => setSubmitDepotContextLoading(false));
  };

  const openUpdateLogistics = (s: ShipmentDTO) => {
    setUpdateRouteModal(s);
    updateRouteForm.setFieldsValue({
      serviceType: s.serviceType || 'D2D',
      transportMode: s.transportMode,
      channelId: s.channelId,
      routeCode: s.routeName || undefined,
    });
  };

  /** 交代理仓：先校验线路，再查代理是否已有单；有则对比确认同步，无则走创建订单弹窗 */
  const openSubmitDepotModal = (s: ShipmentDTO) => {
    if (!s.routeId) {
      message.warning('请先选择线路');
      openUpdateLogistics(s);
      return;
    }
    setCheckAgentOrderLoading(true);
    shipmentApi
      .checkAgentOrder(s.shipmentId)
      .then((res) => {
        const check = res.data;
        if (check?.exists) {
          const cargo = check.cargoItems || [];
          setSyncCargoSelectedKeys(cargo.map((c: AgentCargoItemDTO, i: number) => c.sourceIndex ?? i));
          setSyncConfirmModal({ shipment: s, check });
        } else {
          if (check?.message) {
            message.warning(check.message);
          }
          openCreateDepotFlow(s, check);
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '查询代理运单失败'))
      .finally(() => setCheckAgentOrderLoading(false));
  };

  const onSyncExistingOk = () => {
    if (!syncConfirmModal) return;
    const { shipment } = syncConfirmModal;
    const selectedIndexes = syncCargoSelectedKeys
      .map((k) => Number(k))
      .filter((n) => !Number.isNaN(n));
    const syncCargo = selectedIndexes.length > 0;
    setSyncConfirmSubmitting(true);
    shipmentApi
      .submitToAgentDepot(shipment.shipmentId, {
        syncExisting: true,
        syncCargo,
        selectedCargoIndexes: syncCargo ? selectedIndexes : [],
        agentInboundNo: syncConfirmModal.check.agentInboundNo || undefined,
      })
      .then((res) => {
        const data: SubmitToDepotResultDTO | undefined = res.data;
        const created = data?.createdCartons?.length ?? 0;
        const warnings = data?.warnings || [];
        if (created > 0) {
          message.success(`已同步代理运单并创建 ${created} 箱货物`);
        } else {
          message.success(data?.message || '已同步代理运单并完成交仓');
        }
        if (warnings.length) {
          message.warning(warnings.slice(0, 3).join('；'));
        }
        setSyncConfirmModal(null);
        setSyncCargoSelectedKeys([]);
        load();
        if (detailDrawerOpen && detailShipment?.shipmentId === shipment.shipmentId) {
          shipmentApi.get(shipment.shipmentId).then((r) => setDetailShipment(r.data));
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '同步失败'))
      .finally(() => setSyncConfirmSubmitting(false));
  };

  const onUpdateRouteOk = () => {
    const s = updateRouteModal!;
    updateRouteForm.validateFields().then((values) => {
      setUpdateRouteSubmitting(true);
      const isPre = getShipmentStatus(s) === 'PRE_DECLARATION';
      shipmentApi
        .updateLogistics(s.shipmentId, {
          serviceType: values.serviceType,
          transportMode: values.transportMode,
          channelId: values.channelId || null,
          routeCode: isPre ? (values.routeCode || null) : undefined,
        })
        .then(() => {
          message.success('运输方式已更新');
          setUpdateRouteModal(null);
          updateRouteForm.resetFields();
          load();
          if (detailDrawerOpen && detailShipment?.shipmentId === s.shipmentId) {
            shipmentApi.get(s.shipmentId).then((res) => setDetailShipment(res.data));
          }
        })
        .catch((e) => message.error(e.response?.data?.message || '更新失败'))
        .finally(() => setUpdateRouteSubmitting(false));
    });
  };

  const onSubmitDepotOk = () => {
    const s = submitDepotModal!;
    const agentId = submitDepotContext?.agent?.basicInfo?.identifier;
    const forceBind = !!submitDepotForm.getFieldValue('forceBind');
    if (forceBind && isIntelinkQ5Identifier(agentId)) {
      setSubmitDepotSubmitting(true);
      shipmentApi
        .submitToAgentDepot(s.shipmentId, { syncExisting: true, forceBind: true })
        .then(() => {
          message.success('已强制绑定代理运单并完成交仓');
          setSubmitDepotModal(null);
          setSubmitDepotContext(null);
          setCheckAgentMiss(null);
          submitDepotForm.resetFields();
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '强制绑定失败'))
        .finally(() => setSubmitDepotSubmitting(false));
      return;
    }
    if (isIntelinkQ5Identifier(agentId)) {
      const missing = missingQ5ConsigneeFields(s.clientAddr, s.clientCode);
      if (missing.length) {
        message.warning(
          `哲盟Q5 交仓缺少：${missing.join('、')}。请完善客户地址（含联系人电话）后重试，或更新运单地址为结构化信息。`
        );
        return;
      }
    }
    submitDepotForm.validateFields().then((values) => {
      const body: SubmitToDepotRequest = {
        depotId: values.depotId,
        hubInCode: values.hubInCode?.trim() || undefined,
        huolalaOrderNo: values.huolalaOrderNo?.trim() || undefined,
        photoUrls: values.photoUrls ? values.photoUrls.trim().split(/\s+/).filter(Boolean) : undefined,
        weig: values.weig != null && Number(values.weig) > 0 ? Number(values.weig) : undefined,
      };
      setSubmitDepotSubmitting(true);
      shipmentApi
        .submitToAgentDepot(s.shipmentId, body)
        .then(() => {
          message.success('已提交代理仓');
          setSubmitDepotModal(null);
          setSubmitDepotContext(null);
          setCheckAgentMiss(null);
          submitDepotForm.resetFields();
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '操作失败'))
        .finally(() => setSubmitDepotSubmitting(false));
    });
  };

  const onGenerateInvoiceOk = () => {
    const s = submitInvoiceModal!;
    shipmentApi
      .generateInvoice(s.shipmentId)
      .then((res) => {
        message.success('发票已生成：' + (res.data?.fileName || ''));
        setSubmitInvoiceModal(null);
        load();
        if (detailDrawerOpen && detailShipment?.shipmentId === s.shipmentId) {
          refreshDetailInvoices();
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '生成发票失败'));
  };

  const onDownloadInvoice = (shipmentId: string, inv: ShipmentInvoiceDTO) => {
    shipmentApi
      .downloadInvoice(shipmentId, inv.id)
      .then((res) => {
        const blob = res.data as Blob;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = inv.fileName || 'invoice.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((e) => message.error(e.response?.data?.message || '下载失败'));
  };

  const onSyncTrackingDataOk = () => {
    const s = syncTrackingDataModal!;
    setSyncTrackingDataSubmitting(true);
    shipmentApi
      .syncTrackingData(s.shipmentId)
      .then((res) => {
        const data = res.data as SyncTrackingDataResultDTO;
        if (data?.statusAdvanced) {
          message.success(data.message || `轨迹已同步，状态已更新为 ${statusLabel[data.newStatus || ''] || data.newStatus}`);
        } else {
          message.success(data?.message || '已同步轨迹');
        }
        setSyncTrackingDataModal(null);
        load();
        if (detailDrawerOpen && detailShipment?.shipmentId === s.shipmentId) {
          shipmentApi.get(s.shipmentId).then((r) => setDetailShipment(r.data));
          shipmentApi.getTrackingDisplay(s.shipmentId).then((r) => setDetailTrackingDisplay(r.data ?? null)).catch(() => setDetailTrackingDisplay(null));
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'))
      .finally(() => setSyncTrackingDataSubmitting(false));
  };

  /** 详情抽屉内同步轨迹：拉代理轨迹并按推断推进状态 */
  const syncTrackingDataFromDrawer = () => {
    if (!detailShipment?.shipmentId || !detailShipment.agentInboundNo?.trim()) {
      message.warning('当前运单无代理入库单号，请先执行「交代理仓」');
      return;
    }
    if (detailShipment.status === 'DELIVERED') {
      message.warning('运单已签收，无需同步轨迹');
      return;
    }
    setSyncTrackingDataFromDrawerLoading(true);
    shipmentApi
      .syncTrackingData(detailShipment.shipmentId)
      .then((res) => {
        const data = res.data as SyncTrackingDataResultDTO;
        if (data?.statusAdvanced) {
          message.success(data.message || `轨迹已同步，状态已更新为 ${statusLabel[data.newStatus || ''] || data.newStatus}`);
        } else {
          message.success(data?.message || '已同步轨迹');
        }
        return shipmentApi.get(detailShipment.shipmentId).then((r) => {
          setDetailShipment(r.data);
          load();
          return shipmentApi.getTrackingDisplay(detailShipment.shipmentId).then((tr) => setDetailTrackingDisplay(tr.data ?? null)).catch(() => setDetailTrackingDisplay(null));
        });
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'))
      .finally(() => setSyncTrackingDataFromDrawerLoading(false));
  };

  const onSyncAgentDataOk = () => {
    const s = syncAgentDataModal!;
    setSyncAgentDataSubmitting(true);
    shipmentApi
      .syncAgentData(s.shipmentId)
      .then((res) => {
        const data = res.data as SyncAgentDataResultDTO;
        if (data?.cargoSyncPending) {
          message.warning(data.message || '已同步；箱数不一致，请在详情「绑定的货物」处处理');
        } else {
          message.success(data?.message || '已同步代理入仓数据');
        }
        setSyncAgentDataModal(null);
        load();
        if (detailDrawerOpen && detailShipment?.shipmentId === s.shipmentId) {
          shipmentApi.get(s.shipmentId).then((r) => setDetailShipment(r.data));
          refreshDetailCartons();
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'))
      .finally(() => setSyncAgentDataSubmitting(false));
  };

  /** 在详情抽屉内点击「同步代理入仓数据」：同步成功后刷新当前详情 */
  const syncAgentDataFromDrawer = () => {
    if (!detailShipment?.shipmentId || !detailShipment.agentInboundNo?.trim()) {
      message.warning('当前运单无代理入库单号，请先执行「交代理仓」');
      return;
    }
    setSyncAgentDataFromDrawerLoading(true);
    shipmentApi
      .syncAgentData(detailShipment.shipmentId)
      .then((res) => {
        const data = res.data as SyncAgentDataResultDTO;
        if (data?.cargoSyncPending) {
          message.warning(data.message || '已同步；箱数不一致，请在下方「绑定的货物」处处理');
        } else {
          message.success(data?.message || '已同步代理入仓数据');
        }
        return shipmentApi.get(detailShipment!.shipmentId);
      })
      .then((res) => {
        setDetailShipment(res.data);
        refreshDetailCartons();
      })
      .catch((e) => message.error(e.response?.data?.message || '同步失败'))
      .finally(() => setSyncAgentDataFromDrawerLoading(false));
  };

  const resolveCargoSync = (mode: 'REPLACE' | 'MERGE' | 'LATER') => {
    if (!detailShipment?.shipmentId) return Promise.resolve();
    setResolveCargoSyncLoading(true);
    return shipmentApi
      .resolveCargoSync(detailShipment.shipmentId, { mode })
      .then((res) => {
        const data = res.data;
        if (data?.warnings?.length) {
          message.warning(data.message || '已处理，请查看警告');
          Modal.info({
            title: '处理结果',
            content: (
              <div>
                <p>{data.message}</p>
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                  {data.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            ),
          });
        } else {
          message.success(data?.message || '已处理');
        }
        return shipmentApi.get(detailShipment!.shipmentId);
      })
      .then((res) => {
        setDetailShipment(res.data);
        refreshDetailCartons();
        load();
      })
      .catch((e) => {
        message.error(e.response?.data?.message || '处理失败');
        return Promise.reject(e);
      })
      .finally(() => setResolveCargoSyncLoading(false));
  };

  const getShipmentStatus = (s: ShipmentDTO) => String(s?.status ?? '').trim().toUpperCase();

  const getStatusDisplay = (row: ListRow): string => {
    const st = getShipmentStatus(row.s);
    return statusLabel[st] || row.s.status || st;
  };

  const openRemarkModal = (s: ShipmentDTO) => {
    setRemarkModal(s);
    remarkForm.setFieldsValue({ remark: s.remark || '' });
  };

  const onSaveRemark = () => {
    if (!remarkModal) return;
    remarkForm.validateFields().then((values) => {
      setRemarkSubmitting(true);
      const nextRemark = (values.remark || '').trim();
      shipmentApi
        .updateRemark(remarkModal.shipmentId, nextRemark)
        .then(() => {
          message.success('备注已保存');
          setRemarkModal(null);
          remarkForm.resetFields();
          load();
          if (detailShipment?.shipmentId === remarkModal.shipmentId) {
            setDetailShipment({ ...detailShipment, remark: nextRemark || undefined });
          }
        })
        .catch((e) => message.error(e.response?.data?.message || '保存备注失败'))
        .finally(() => setRemarkSubmitting(false));
    });
  };

  const openChannelShipmentModal = (s: ShipmentDTO) => {
    setChannelShipmentModal(s);
    channelShipmentForm.setFieldsValue({ channelShipmentId: s.channelShipmentId || '' });
  };

  const onSaveChannelShipmentId = () => {
    if (!channelShipmentModal) return;
    channelShipmentForm.validateFields().then((values) => {
      setChannelShipmentSubmitting(true);
      const nextChannelShipmentId = (values.channelShipmentId || '').trim();
      shipmentApi
        .setChannelShipmentId(channelShipmentModal.shipmentId, nextChannelShipmentId)
        .then(() => {
          message.success('转单号已保存');
          setChannelShipmentModal(null);
          channelShipmentForm.resetFields();
          load();
          if (detailShipment?.shipmentId === channelShipmentModal.shipmentId) {
            setDetailShipment({
              ...detailShipment,
              channelShipmentId: nextChannelShipmentId || undefined,
            });
          }
        })
        .catch((e) => message.error(e.response?.data?.message || '保存转单号失败'))
        .finally(() => setChannelShipmentSubmitting(false));
    });
  };

  /** 操作列：Dropdown 压缩空间；禁用项保留原因提示 */
  const renderActions = (row: ListRow) => {
    const s = row.s;
    const st = getShipmentStatus(s);
    if (st === 'DELIVERED') return <span style={{ color: '#999' }}>已签收</span>;

    const canBind = st === 'PRE_DECLARATION';
    const canSubmitDepot = st === 'PRE_DECLARATION';
    const canUpdateLogistics = st !== 'DELIVERED';
    const canWeight = st === 'SUBMITTED_TO_DEPOT' || st === 'HANDOVER_CONFIRMING';
    const canSubmitInvoice = st === 'SUBMITTED_TO_DEPOT';
    const canSyncAgentData = !!(s.agentInboundNo && s.agentInboundNo.trim());
    const canSyncTracking = !!(s.agentInboundNo && s.agentInboundNo.trim()) && st !== 'DELIVERED';

    type ActionDef = { key: string; label: string; allowed: boolean; disabledReason: string; onClick: () => void };
    const defs: ActionDef[] = [
      {
        key: 'bind',
        label: '绑定货物',
        allowed: canBind,
        disabledReason: '当前状态不允许执行此操作，需为预申报',
        onClick: () => openBindModal(s),
      },
      {
        key: 'update-route',
        label: '更新运输方式',
        allowed: canUpdateLogistics,
        disabledReason: '已签收运单不可修改',
        onClick: () => openUpdateLogistics(s),
      },
      {
        key: 'submit-depot',
        label: '交代理仓',
        allowed: canSubmitDepot && !checkAgentOrderLoading,
        disabledReason: '当前状态不允许执行此操作，需为预申报',
        onClick: () => openSubmitDepotModal(s),
      },
      {
        key: 'weight',
        label: '确认重量',
        allowed: canWeight,
        disabledReason: '当前状态不允许执行此操作，需为已交代理仓或交货确认中',
        onClick: () => setWeightModal(s),
      },
      {
        key: 'generate-invoice',
        label: '生成发票',
        allowed: canSubmitInvoice,
        disabledReason: '当前状态不允许执行此操作，需为已交代理仓',
        onClick: () => setSubmitInvoiceModal(s),
      },
      {
        key: 'sync-agent',
        label: '同步代理入仓数据',
        allowed: canSyncAgentData,
        disabledReason: '请先执行「交代理仓」并获取代理入库单号后再同步',
        onClick: () => setSyncAgentDataModal(s),
      },
      {
        key: 'sync-tracking',
        label: '同步轨迹',
        allowed: canSyncTracking,
        disabledReason: '请先执行「交代理仓」并获取代理入库单号；已签收运单不可同步',
        onClick: () => setSyncTrackingDataModal(s),
      },
    ];

    const items: MenuProps['items'] = defs.map((d) => ({
      key: d.key,
      disabled: !d.allowed,
      label: d.allowed ? (
        d.label
      ) : (
        <Tooltip title={d.disabledReason} placement="left">
          <span>{d.label}</span>
        </Tooltip>
      ),
      onClick: d.allowed
        ? () => {
            d.onClick();
          }
        : undefined,
    }));

    return (
      <Dropdown menu={{ items }} trigger={['click']}>
        <Button type="link" size="small" style={{ padding: 0 }}>
          操作 <DownOutlined />
        </Button>
      </Dropdown>
    );
  };

  return (
    <>
      <Card
        title="运单列表"
        style={{ marginBottom: 16 }}
        extra={
          <Button type="primary" onClick={() => setCreateModalOpen(true)}>
            创建预申报
          </Button>
        }
      >
        <Input.Search
          placeholder="客户编号"
          allowClear
          style={{ width: 200, marginRight: 8 }}
          onSearch={setClientCode}
        />
        <Input
          placeholder="模糊搜索运单号"
          allowClear
          style={{ width: 200, marginRight: 8 }}
          value={filterShipmentIdInput}
          onChange={(e) => setFilterShipmentIdInput(e.target.value)}
          onPressEnter={applyFilters}
        />
        <Select
          placeholder="状态"
          allowClear
          style={{ width: 140, marginRight: 8 }}
          options={statusOptions}
          onChange={setStatus}
        />
        <Button type="primary" onClick={applyFilters} style={{ marginRight: 8 }}>
          查询
        </Button>
        {appliedShipmentIdKeyword && (
          <Button onClick={resetShipmentIdFilter}>清空运单号</Button>
        )}
      </Card>
      <Card>
        <Table
          loading={loading}
          dataSource={rows}
          rowKey="key"
          scroll={{ x: 1600 }}
          onRow={(record) => ({
            onClick: () => openDetailDrawer(record.s),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: '运单号/预申报单号',
              width: 160,
              ellipsis: true,
              render: (_, r: ListRow) => highlightText(r.s.shipmentId, appliedShipmentIdKeyword),
            },
            {
              title: '客户',
              width: 100,
              render: (_, r: ListRow) => r.s.clientCode,
            },
            {
              title: '线路',
              width: 120,
              render: (_, r: ListRow) => r.s.routeName || '-',
            },
            {
              title: '国家',
              width: 100,
              render: (_, r: ListRow) => r.s.destinationCountry || '-',
            },
            {
              title: '代理',
              width: 100,
              render: (_, r: ListRow) => r.s.agentIdentifier || '-',
            },
            { title: '状态', width: 100, render: (_, r: ListRow) => getStatusDisplay(r) },
            {
              title: '备注',
              width: 140,
              ellipsis: true,
              render: (_, r: ListRow) => {
                const text = r.s.remark?.trim();
                const delivered = getShipmentStatus(r.s) === 'DELIVERED';
                return (
                  <Space size={4} style={{ maxWidth: '100%' }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={text || undefined}>
                      <span style={{ maxWidth: 100, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                        {text || '-'}
                      </span>
                    </Tooltip>
                    {!delivered && (
                      <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        style={{ padding: 0 }}
                        onClick={() => openRemarkModal(r.s)}
                      />
                    )}
                  </Space>
                );
              },
            },
            {
              title: '代理入库单号',
              width: 120,
              render: (_, r: ListRow) => r.s.agentInboundNo || '-',
            },
            {
              title: '转单号',
              width: 140,
              ellipsis: true,
              render: (_, r: ListRow) => {
                const text = r.s.channelShipmentId?.trim();
                const delivered = getShipmentStatus(r.s) === 'DELIVERED';
                return (
                  <Space size={4} style={{ maxWidth: '100%' }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={text || undefined}>
                      <span style={{ maxWidth: 100, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                        {text || '-'}
                      </span>
                    </Tooltip>
                    {!delivered && (
                      <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        style={{ padding: 0 }}
                        onClick={() => openChannelShipmentModal(r.s)}
                      />
                    )}
                  </Space>
                );
              },
            },
            {
              title: '计费重',
              width: 80,
              render: (_, r: ListRow) => (r.s.confirmedWeight != null ? r.s.confirmedWeight : '-'),
            },
            {
              title: '货物箱数',
              width: 88,
              render: (_, r: ListRow) => r.s.cartonCount ?? 0,
            },
            {
              title: '创建时间',
              width: 150,
              render: (_, r: ListRow) =>
                r.s.createdAt ? dayjs(r.s.createdAt).format('YYYY-MM-DD HH:mm') : '-',
            },
            {
              title: '服务类型',
              width: 90,
              render: (_, r: ListRow) => serviceTypeLabel(r.s.serviceType),
            },
            {
              title: '运输方式',
              width: 90,
              render: (_, r: ListRow) => {
                const m = r.s.transportMode;
                return transportModes.find((t) => t.mode === m)?.displayName || m || '-';
              },
            },
            {
              title: '渠道',
              width: 140,
              ellipsis: true,
              render: (_, r: ListRow) => channelDisplay(r.s),
            },
            {
              title: '操作',
              width: 88,
              fixed: 'right',
              render: (_, r: ListRow) => <span onClick={(e) => e.stopPropagation()}>{renderActions(r)}</span>,
            },
          ]}
          pagination={serverTablePagination({
            page,
            pageSize,
            total,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          })}
        />
      </Card>

      <Modal
        title="创建预申报"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          createForm.resetFields();
          setManualShipmentId(false);
          setAddressDetail(null);
          setAddresses([]);
        }}
        footer={null}
        width={560}
      >
        <Form form={createForm} layout="vertical" onFinish={onFinishCreate} initialValues={{ manualShipmentId: false }}>
          <Form.Item label="手动输入运单号">
            <Switch
              checked={manualShipmentId}
              onChange={(v) => {
                setManualShipmentId(v);
                if (!v) createForm.setFieldValue('shipmentId', undefined);
              }}
            />
            <span style={{ marginLeft: 8, color: '#666', fontSize: 12 }}>默认自动生成（K+年月+4位数字）；开启后可填写代理系统已有运单号</span>
          </Form.Item>
          {manualShipmentId && (
            <Form.Item
              name="shipmentId"
              label="运单号"
              rules={[{ required: true, message: '请输入运单号' }]}
            >
              <Input placeholder="例如代理系统中的客户单号" />
            </Form.Item>
          )}
          <Form.Item name="clientCode" label="客户编号" rules={[{ required: true, message: '请选择客户' }]}>
            <Select
              placeholder="从客户列表选择"
              showSearch
              optionFilterProp="label"
              onChange={onCustomerChange}
              options={customers.map((c) => ({ label: `${c.customerCode} - ${c.name || ''}`, value: c.customerCode }))}
            />
          </Form.Item>
          <Form.Item label="客户地址" required>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="clientAddr" noStyle rules={[{ required: true, message: '请选择地址' }]}>
                <Select
                  style={{ width: '100%' }}
                  placeholder={addresses.length ? '选择地址' : '请先选择客户'}
                  options={addresses.map((a) => ({
                    label: [a.label, a.remark].filter(Boolean).join(' | ') || a.id,
                    value: a.id,
                  }))}
                  disabled={!addresses.length}
                  onChange={() => setAddressDetail(null)}
                />
              </Form.Item>
              <Button
                onClick={() => loadAddressDetail(createForm.getFieldValue('clientAddr'))}
                loading={addressDetailLoading}
              >
                查看详情
              </Button>
            </Space.Compact>
          </Form.Item>
          {addressDetail && (
            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fafafa', borderRadius: 4, fontSize: 13, color: '#555' }}>
              <div>联系人：{addressDetail.contactName || '-'}</div>
              <div>电话：{addressDetail.phone || '-'}</div>
              <div>地址：{addressDetail.address || '-'}</div>
              <div>邮编：{addressDetail.postalCode || '-'}</div>
            </div>
          )}
          <Form.Item name="serviceType" label="服务类型" rules={[{ required: true, message: '请选择服务类型' }]} initialValue="D2D">
            <Select options={SERVICE_TYPE_OPTIONS} placeholder="请选择服务类型" />
          </Form.Item>
          <Form.Item name="transportMode" label="运输方式" rules={[{ required: true, message: '请选择运输方式' }]}>
            <Select
              placeholder="请选择运输方式"
              options={transportModes.map((t) => ({
                label: t.displayName || t.mode,
                value: t.mode,
              }))}
              onChange={() => createForm.setFieldsValue({ routeCode: undefined })}
            />
          </Form.Item>
          <Form.Item name="channelId" label="渠道">
            <Select
              allowClear
              placeholder="可选，后续可追加"
              options={channels.map((c) => ({
                label: `${c.channelCode}${c.channelName ? ` (${c.channelName})` : ''}`,
                value: c.id,
              }))}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.transportMode !== cur.transportMode}>
            {() => {
              const mode = createForm.getFieldValue('transportMode') as string | undefined;
              const filtered = mode
                ? routes.filter((r) => (r.transportMode || '').toUpperCase() === mode.toUpperCase())
                : routes;
              return (
                <Form.Item name="routeCode" label="线路">
                  <Select
                    allowClear
                    placeholder="可选，交代理仓前需补全"
                    options={filtered.map((r) => ({
                      label: `${r.routeCode}${r.serviceType ? ` (${r.serviceType})` : ''}`,
                      value: r.routeCode,
                    }))}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item name="expectedShipDate" label="预计发货日">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="cargoDescJson" label="货物描述">
            <Input.TextArea rows={3} placeholder="请输入货物描述（文本）" />
          </Form.Item>
          <Form.Item
            name="remark"
            label="备注"
            rules={[{ max: REMARK_MAX_LENGTH, message: `备注最长 ${REMARK_MAX_LENGTH} 字` }]}
          >
            <Input.TextArea rows={2} placeholder="可选" maxLength={REMARK_MAX_LENGTH} showCount />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              确定创建
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setCreateModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="绑定货物"
        open={bindModalOpen}
        onCancel={() => {
          setBindModalOpen(false);
          setBindingShipment(null);
        }}
        footer={null}
        width={720}
      >
        {bindingShipment && (
          <div style={{ marginBottom: 16 }}>
            <span style={{ marginRight: 8 }}>当前运单：</span>
            <strong>{bindingShipment.shipmentId}</strong>
            <span style={{ marginLeft: 8, color: '#666' }}>（客户 {bindingShipment.clientCode}）</span>
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={onBindCartons} loading={bindCartonLoading} disabled={selectedCartonIds.length === 0}>
            绑定选中
          </Button>
        </div>
        <Table
          rowSelection={{
            selectedRowKeys: selectedCartonIds,
            onChange: (keys) => setSelectedCartonIds(keys as string[]),
          }}
          dataSource={cartonsForBind}
          rowKey="id"
          size="small"
          columns={[
            { title: '货物标识', dataIndex: 'cartonId', width: 160, ellipsis: true },
            { title: '客户', dataIndex: 'belongClientId', width: 120, render: (v: string) => customers.find((c) => c.id === v || c.customerCode === v)?.name || v || '-' },
            { title: '状态', dataIndex: 'state', width: 100, render: (v: string) => cartonStateLabel[v || ''] || v || '-' },
            { title: '创建时间', dataIndex: 'createTimestamp', width: 160, render: (t: number) => (t ? new Date(t).toLocaleString() : '-') },
          ]}
          pagination={clientTablePagination({ defaultPageSize: 5 })}
        />
      </Modal>

      <Modal
        title="确认重量"
        open={!!weightModal}
        onOk={onWeightOk}
        onCancel={() => {
          setWeightModal(null);
          weightForm.resetFields();
        }}
      >
        {weightModal && (
          <Form form={weightForm} layout="vertical">
            <Form.Item
              name="confirmedWeight"
              label="确认计费重(kg)"
              rules={[{ required: true }]}
              initialValue={weightModal.pendingWeight}
            >
              <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title="交代理仓"
        open={!!submitDepotModal}
        onOk={onSubmitDepotOk}
        onCancel={() => {
          setSubmitDepotModal(null);
          setSubmitDepotContext(null);
          setCheckAgentMiss(null);
          setManualAgentInboundNo('');
          submitDepotForm.resetFields();
        }}
        confirmLoading={submitDepotSubmitting}
        width={560}
        destroyOnClose
      >
        {submitDepotModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{submitDepotModal.shipmentId}</strong> 将提交至代理仓，请选择目标仓库并填写交仓信息。
            </p>
            {checkAgentMiss && !checkAgentMiss.exists && (
              <div style={{ marginBottom: 12, padding: 10, background: '#fffbe6', borderRadius: 6, fontSize: 12, color: '#ad6800' }}>
                {checkAgentMiss.needManualInboundNo || checkAgentMiss.agentIdentifier === '56yorky'
                  ? checkAgentMiss.message ||
                    '已下单但是仓库还未出数据，需手动填入入仓单号或稍后再试'
                  : `代理侧未查到已有运单${checkAgentMiss.message ? `：${checkAgentMiss.message}` : '。'}`}
                {isIntelinkQ5Identifier(checkAgentMiss.agentIdentifier || submitDepotContext?.agent?.basicInfo?.identifier) && (
                  <> 若确认哲盟已有该单号，可勾选下方「强制绑定」直接同步，无需新建。</>
                )}
                {(checkAgentMiss.needManualInboundNo ||
                  (checkAgentMiss.agentIdentifier || '').toLowerCase() === '56yorky') && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ marginBottom: 6, color: 'rgba(0,0,0,0.65)' }}>代理入仓单号</div>
                    <Space.Compact style={{ width: '100%' }}>
                      <Input
                        placeholder="例如 11089610"
                        value={manualAgentInboundNo}
                        onChange={(e) => setManualAgentInboundNo(e.target.value)}
                        allowClear
                      />
                      <Button
                        type="primary"
                        loading={manualInboundVerifying}
                        onClick={() => {
                          const inbound = manualAgentInboundNo.trim();
                          if (!inbound) {
                            message.warning('请填写代理入仓单号');
                            return;
                          }
                          if (!submitDepotModal) return;
                          setManualInboundVerifying(true);
                          shipmentApi
                            .checkAgentOrder(submitDepotModal.shipmentId, { agentInboundNo: inbound })
                            .then((res) => {
                              const check = res.data;
                              if (check?.exists) {
                                const cargo = check.cargoItems || [];
                                setSyncCargoSelectedKeys(
                                  cargo.map((c: AgentCargoItemDTO, i: number) => c.sourceIndex ?? i)
                                );
                                setSyncConfirmModal({ shipment: submitDepotModal, check });
                                setSubmitDepotModal(null);
                                setSubmitDepotContext(null);
                                setCheckAgentMiss(null);
                                setManualAgentInboundNo('');
                                submitDepotForm.resetFields();
                                message.success('入仓单号校验通过，请确认后同步');
                              } else {
                                setCheckAgentMiss(check || null);
                                message.error(check?.message || '入仓单号校验失败');
                              }
                            })
                            .catch((e) => message.error(e.response?.data?.message || '校验失败'))
                            .finally(() => setManualInboundVerifying(false));
                        }}
                      >
                        校验并同步
                      </Button>
                    </Space.Compact>
                    <div style={{ marginTop: 4, color: 'rgba(0,0,0,0.45)' }}>
                      从 56yorky 后台复制入库号；将校验客户单号与本运单一致
                    </div>
                  </div>
                )}
              </div>
            )}
            {submitDepotContextLoading ? (
              <Spin spinning />
            ) : submitDepotContext ? (
              <>
                {submitDepotContext.agent && (
                  <div style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 8 }}>
                    <div style={{ marginBottom: 4 }}>
                      <strong>代理：</strong>
                      {submitDepotContext.agent.basicInfo?.name || submitDepotContext.agent.basicInfo?.identifier || '-'}
                    </div>
                    {submitDepotContext.agent.contact?.contactPerson && (
                      <div style={{ color: '#666', fontSize: 12 }}>
                        对接人：{submitDepotContext.agent.contact.contactPerson.name} {submitDepotContext.agent.contact.contactPerson.phone}
                      </div>
                    )}
                    {isIntelinkQ5Identifier(submitDepotContext.agent.basicInfo?.identifier) &&
                      missingQ5ConsigneeFields(submitDepotModal.clientAddr, submitDepotModal.clientCode).length > 0 && (
                        <div style={{ marginTop: 8, color: '#d46b08', fontSize: 12 }}>
                          哲盟Q5 需要完整收件信息，当前缺少：
                          {missingQ5ConsigneeFields(submitDepotModal.clientAddr, submitDepotModal.clientCode).join('、')}
                          。请先完善客户地址簿中的联系人电话后重建预申报，或更新本运单 clientAddr。
                        </div>
                      )}
                  </div>
                )}
                <Form form={submitDepotForm} layout="vertical">
                  {isIntelinkQ5Identifier(submitDepotContext.agent?.basicInfo?.identifier) &&
                    checkAgentMiss &&
                    !checkAgentMiss.exists && (
                      <Form.Item name="forceBind" valuePropName="checked">
                        <Checkbox>确认哲盟已有该运单号，强制绑定同步（不创建订单）</Checkbox>
                      </Form.Item>
                    )}
                  <Form.Item noStyle shouldUpdate={(prev, cur) => prev.forceBind !== cur.forceBind}>
                    {() => {
                      const force = !!submitDepotForm.getFieldValue('forceBind');
                      return (
                        <>
                          <Form.Item
                            name="depotId"
                            label="代理仓库"
                            rules={force ? [] : [{ required: true, message: '请选择目标仓库' }]}
                          >
                            <Select
                              placeholder="选择仓库"
                              disabled={force}
                              options={(submitDepotContext.depots || []).map((d) => ({
                                label: d.depotName ? `${d.depotName}${d.depotId ? ` (${d.depotId})` : ''}` : String(d.depotId ?? d.id ?? ''),
                                value: String(d.depotId ?? d.id ?? ''),
                              }))}
                              allowClear={false}
                            />
                          </Form.Item>
                          {isIntelinkQ5Identifier(submitDepotContext.agent?.basicInfo?.identifier) && !force && (
                            <>
                              <Form.Item
                                name="hubInCode"
                                label="服务渠道（hubInCode）"
                                rules={
                                  (submitDepotContext.serviceChannels || []).some((c) => c.serviceCode)
                                    ? [{ required: true, message: '请选择哲盟服务渠道' }]
                                    : []
                                }
                                extra="对应 getProductList 的渠道编码；须先在代理管理中同步服务渠道"
                              >
                                <Select
                                  placeholder="选择服务渠道"
                                  options={(submitDepotContext.serviceChannels || [])
                                    .filter((c) => c.serviceCode)
                                    .map((c) => ({
                                      label: c.serviceName
                                        ? `${c.serviceName} (${c.serviceCode})`
                                        : String(c.serviceCode),
                                      value: String(c.serviceCode),
                                    }))}
                                  showSearch
                                  optionFilterProp="label"
                                  allowClear={false}
                                />
                              </Form.Item>
                              <Form.Item
                                name="weig"
                                label="交仓重量 weig（kg）"
                                rules={
                                  submitDepotContext.weigRequiredForCreate
                                    ? [{ required: true, message: '哲盟Q5 新建订单需要重量' }]
                                    : []
                                }
                                extra={
                                  submitDepotContext.suggestedWeig != null
                                    ? `已根据运单/货物汇总建议 ${submitDepotContext.suggestedWeig} kg，可修改`
                                    : '运单与货物均无重量时必填'
                                }
                              >
                                <InputNumber min={0.001} step={0.1} style={{ width: '100%' }} placeholder="kg" />
                              </Form.Item>
                            </>
                          )}
                        </>
                      );
                    }}
                  </Form.Item>
                  <Form.Item name="huolalaOrderNo" label="货拉拉单号">
                    <Input placeholder="选填" />
                  </Form.Item>
                  <Form.Item name="photoUrls" label="照片（多张 URL 空格分隔）">
                    <Input.TextArea rows={2} placeholder="选填，多张照片 URL 用空格分隔" />
                  </Form.Item>
                </Form>
              </>
            ) : null}
          </>
        )}
      </Modal>

      <Modal
        title="同步代理已有运单"
        open={!!syncConfirmModal}
        onOk={onSyncExistingOk}
        onCancel={() => {
          setSyncConfirmModal(null);
          setSyncCargoSelectedKeys([]);
        }}
        okText={
          syncCargoSelectedKeys.length > 0
            ? `同步运单+货物（${syncCargoSelectedKeys.length} 箱）`
            : '同步运单'
        }
        confirmLoading={syncConfirmSubmitting}
        width={860}
        destroyOnClose
      >
        {syncConfirmModal && (
          <>
            <p style={{ marginBottom: 16 }}>
              代理系统中已存在运单 <strong>{syncConfirmModal.shipment.shipmentId}</strong>，请对比确认后同步绑定（不会在代理侧新建订单）。
            </p>
            <Table
              size="small"
              pagination={false}
              rowKey="field"
              dataSource={[
                {
                  field: '运单号/客户单号',
                  kfy: syncConfirmModal.check.kfy?.shipmentId || '-',
                  agent: syncConfirmModal.check.agent?.userNo || '-',
                },
                {
                  field: '代理入库单号',
                  kfy: '-',
                  agent: syncConfirmModal.check.agent?.inputId || syncConfirmModal.check.agentInboundNo || '-',
                },
                {
                  field: '客户',
                  kfy: syncConfirmModal.check.kfy?.clientCode || '-',
                  agent: '-',
                },
                {
                  field: '线路 / 服务渠道',
                  kfy: syncConfirmModal.check.kfy?.routeName || '-',
                  agent: syncConfirmModal.check.agent?.serviceName || '-',
                },
                {
                  field: '货物件数',
                  kfy: String(syncConfirmModal.check.kfy?.cartonCount ?? 0),
                  agent: String(syncConfirmModal.check.cargoItems?.length ?? 0),
                },
                {
                  field: '状态 / 最新轨迹',
                  kfy: statusLabel[syncConfirmModal.check.kfy?.status || ''] || syncConfirmModal.check.kfy?.status || '-',
                  agent: syncConfirmModal.check.agent?.trackSummary || '-',
                },
              ]}
              columns={[
                { title: '字段', dataIndex: 'field', width: 140 },
                { title: '凯飞越', dataIndex: 'kfy' },
                { title: '代理系统', dataIndex: 'agent' },
              ]}
            />
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <strong>代理货物清单</strong>
                <Space>
                  <Button
                    size="small"
                    onClick={() => {
                      const cargo = syncConfirmModal.check.cargoItems || [];
                      setSyncCargoSelectedKeys(cargo.map((c, i) => c.sourceIndex ?? i));
                    }}
                  >
                    全选
                  </Button>
                  <Button size="small" onClick={() => setSyncCargoSelectedKeys([])}>
                    取消
                  </Button>
                </Space>
              </div>
              {syncConfirmModal.check.cargoFetchMessage && (
                <div style={{ marginBottom: 8, color: '#ad6800', fontSize: 12 }}>
                  {syncConfirmModal.check.cargoFetchMessage}
                </div>
              )}
              <Table
                size="small"
                rowKey={(r) => String(r.sourceIndex ?? r.cartonId)}
                pagination={false}
                scroll={{ y: 240 }}
                rowSelection={{
                  selectedRowKeys: syncCargoSelectedKeys,
                  onChange: (keys) => setSyncCargoSelectedKeys(keys),
                }}
                dataSource={syncConfirmModal.check.cargoItems || []}
                locale={{ emptyText: '暂无代理货物测量数据' }}
                columns={[
                  { title: '箱号', dataIndex: 'cartonId', width: 160, ellipsis: true },
                  {
                    title: '长×宽×高(cm)',
                    width: 140,
                    render: (_: unknown, r: AgentCargoItemDTO) =>
                      [r.lengthCm, r.widthCm, r.heightCm].every((v) => v != null)
                        ? `${r.lengthCm}×${r.widthCm}×${r.heightCm}`
                        : '-',
                  },
                  {
                    title: '毛重(kg)',
                    dataIndex: 'measuredWeightKg',
                    width: 90,
                    render: (v: number) => (v != null ? v : '-'),
                  },
                  {
                    title: '计费重(kg)',
                    dataIndex: 'billingWeightKg',
                    width: 100,
                    render: (v: number) => (v != null ? v : '-'),
                  },
                  {
                    title: '体积',
                    dataIndex: 'volumeCbm',
                    width: 80,
                    render: (v: number) => (v != null ? v : '-'),
                  },
                  { title: '库位', dataIndex: 'locationCode', width: 100, ellipsis: true, render: (v: string) => v || '-' },
                ]}
              />
            </div>
          </>
        )}
      </Modal>

      <Modal
        title="更新运输方式"
        open={!!updateRouteModal}
        onOk={onUpdateRouteOk}
        onCancel={() => {
          setUpdateRouteModal(null);
          updateRouteForm.resetFields();
        }}
        confirmLoading={updateRouteSubmitting}
        destroyOnClose
      >
        {updateRouteModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{updateRouteModal.shipmentId}</strong>
              {getShipmentStatus(updateRouteModal) === 'DELIVERED' ? null : (
                <span style={{ marginLeft: 8, color: '#666', fontSize: 12 }}>
                  可修改运输方式；变更后若与已绑线路不一致将自动清空线路
                </span>
              )}
            </p>
            <Form form={updateRouteForm} layout="vertical">
              <Form.Item name="serviceType" label="服务类型" rules={[{ required: true, message: '请选择服务类型' }]}>
                <Select options={SERVICE_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item name="transportMode" label="运输方式" rules={[{ required: true, message: '请选择运输方式' }]}>
                <Select
                  options={transportModes.map((t) => ({
                    label: t.displayName || t.mode,
                    value: t.mode,
                  }))}
                  onChange={() => updateRouteForm.setFieldsValue({ routeCode: undefined })}
                />
              </Form.Item>
              <Form.Item name="channelId" label="渠道">
                <Select
                  allowClear
                  placeholder="可选"
                  options={channels.map((c) => ({
                    label: `${c.channelCode}${c.channelName ? ` (${c.channelName})` : ''}`,
                    value: c.id,
                  }))}
                />
              </Form.Item>
              {getShipmentStatus(updateRouteModal) === 'PRE_DECLARATION' && (
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.transportMode !== cur.transportMode}>
                  {() => {
                    const mode = updateRouteForm.getFieldValue('transportMode') as string | undefined;
                    const filtered = mode
                      ? routes.filter((r) => (r.transportMode || '').toUpperCase() === mode.toUpperCase())
                      : routes;
                    return (
                      <Form.Item name="routeCode" label="线路">
                        <Select
                          allowClear
                          placeholder="可选"
                          options={filtered.map((r) => ({
                            label: `${r.routeCode}${r.serviceType ? ` (${r.serviceType})` : ''}`,
                            value: r.routeCode,
                          }))}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              )}
            </Form>
          </>
        )}
      </Modal>

      <Modal
        title="编辑转单号"
        open={!!channelShipmentModal}
        onOk={onSaveChannelShipmentId}
        onCancel={() => {
          setChannelShipmentModal(null);
          channelShipmentForm.resetFields();
        }}
        confirmLoading={channelShipmentSubmitting}
        destroyOnClose
      >
        {channelShipmentModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{channelShipmentModal.shipmentId}</strong>
            </p>
            <Form form={channelShipmentForm} layout="vertical">
              <Form.Item
                name="channelShipmentId"
                label="转单号"
                rules={[{ max: CHANNEL_SHIPMENT_ID_MAX_LENGTH, message: `转单号最长 ${CHANNEL_SHIPMENT_ID_MAX_LENGTH} 字` }]}
              >
                <Input placeholder="UPS/DHL 等 label 单号，可清空" maxLength={CHANNEL_SHIPMENT_ID_MAX_LENGTH} showCount />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      <Modal
        title="编辑备注"
        open={!!remarkModal}
        onOk={onSaveRemark}
        onCancel={() => {
          setRemarkModal(null);
          remarkForm.resetFields();
        }}
        confirmLoading={remarkSubmitting}
        destroyOnClose
      >
        {remarkModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{remarkModal.shipmentId}</strong>
            </p>
            <Form form={remarkForm} layout="vertical">
              <Form.Item
                name="remark"
                label="备注"
                rules={[{ max: REMARK_MAX_LENGTH, message: `备注最长 ${REMARK_MAX_LENGTH} 字` }]}
              >
                <Input.TextArea rows={3} placeholder="可清空" maxLength={REMARK_MAX_LENGTH} showCount />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      <Modal
        title="生成发票"
        open={!!submitInvoiceModal}
        onOk={onGenerateInvoiceOk}
        onCancel={() => setSubmitInvoiceModal(null)}
      >
        {submitInvoiceModal && (
          <p>确定要为运单 <strong>{submitInvoiceModal.shipmentId}</strong> 生成发票 Excel？将根据运单、路线、客户、地址及产品信息按模板生成文件。</p>
        )}
      </Modal>

      <Modal
        title="同步代理入仓数据"
        open={!!syncAgentDataModal}
        onOk={onSyncAgentDataOk}
        onCancel={() => setSyncAgentDataModal(null)}
        confirmLoading={syncAgentDataSubmitting}
      >
        {syncAgentDataModal && (
          <p>
            确定对运单 <strong>{syncAgentDataModal.shipmentId}</strong> 执行同步代理入仓数据？将从代理系统拉取入库状态、出库状态、测量数据，并更新运单及该运单下货物状态（不含轨迹）。若绑定箱数与代理测量箱数不一致，请到运单详情「绑定的货物」处选择处理方式。
          </p>
        )}
      </Modal>

      <Modal
        title="同步轨迹"
        open={!!syncTrackingDataModal}
        onOk={onSyncTrackingDataOk}
        onCancel={() => setSyncTrackingDataModal(null)}
        confirmLoading={syncTrackingDataSubmitting}
      >
        {syncTrackingDataModal && (
          <p>
            确定对运单 <strong>{syncTrackingDataModal.shipmentId}</strong> 执行同步轨迹？将从代理系统拉取运单轨迹并更新展示；若轨迹含运输/派送/签收语义，将前向更新运单状态（不可回退）。
          </p>
        )}
      </Modal>

      <ResponsiveDrawer
        title={detailView === 'carton' ? '货物详情' : '运单详情'}
        size="xl"
        open={detailDrawerOpen}
        onClose={closeDetailDrawer}
        closable
      >
        {detailView === 'carton' ? (
          <CartonDetailDrawer
            embedded
            open={detailDrawerOpen && !!cartonDetailId}
            cartonId={cartonDetailId}
            onClose={backToShipmentDetail}
            onBack={backToShipmentDetail}
            onSaved={refreshDetailCartons}
          />
        ) : detailShipmentLoading ? (
          <Spin spinning />
        ) : detailShipment ? (
          <>
            <Card
              size="small"
              title={<span style={{ fontWeight: 600, fontSize: 15 }}>基础信息</span>}
              extra={
                getShipmentStatus(detailShipment) !== 'DELIVERED' ? (
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openUpdateLogistics(detailShipment)}>
                    更新运输方式
                  </Button>
                ) : null
              }
              style={{ marginBottom: 16 }}
            >
              <Descriptions column={2} size="small">
                <Descriptions.Item label="运单号">{detailShipment.shipmentId}</Descriptions.Item>
                <Descriptions.Item label="客户">{detailShipment.clientCode}</Descriptions.Item>
                <Descriptions.Item label="服务类型">{serviceTypeLabel(detailShipment.serviceType)}</Descriptions.Item>
                <Descriptions.Item label="运输方式">
                  <Space size={4}>
                    <span>
                      {transportModes.find((t) => t.mode === detailShipment.transportMode)?.displayName
                        || detailShipment.transportMode
                        || '-'}
                    </span>
                    {getShipmentStatus(detailShipment) !== 'DELIVERED' && (
                      <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        style={{ padding: 0 }}
                        onClick={() => openUpdateLogistics(detailShipment)}
                      />
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="渠道">{channelDisplay(detailShipment)}</Descriptions.Item>
                <Descriptions.Item label="线路">{detailShipment.routeName || '-'}</Descriptions.Item>
                <Descriptions.Item label="代理">{detailShipment.agentIdentifier || '-'}</Descriptions.Item>
                <Descriptions.Item label="状态">{statusLabel[getShipmentStatus(detailShipment)] || detailShipment.status}</Descriptions.Item>
                <Descriptions.Item label="代理入库单号">{detailShipment.agentInboundNo || '-'}</Descriptions.Item>
                <Descriptions.Item label="代理订单来源">
                  {detailShipment.agentOrderOrigin === 'SYNCED_FROM_AGENT'
                    ? '从代理同步'
                    : detailShipment.agentOrderOrigin === 'CREATED_IN_KFY'
                      ? '凯飞越创建提交'
                      : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="转单号">
                  <Space size={8}>
                    <span>{detailShipment.channelShipmentId?.trim() || '-'}</span>
                    {getShipmentStatus(detailShipment) !== 'DELIVERED' && (
                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openChannelShipmentModal(detailShipment)}>
                        编辑
                      </Button>
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="计费重">{detailShipment.confirmedWeight != null ? detailShipment.confirmedWeight : '-'}</Descriptions.Item>
                <Descriptions.Item label="创建时间">
                  {detailShipment.createdAt ? dayjs(detailShipment.createdAt).format('YYYY-MM-DD HH:mm') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="备注">
                  <Space size={8}>
                    <span>{detailShipment.remark?.trim() || '-'}</span>
                    {getShipmentStatus(detailShipment) !== 'DELIVERED' && (
                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openRemarkModal(detailShipment)}>
                        编辑
                      </Button>
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="客户地址" span={2}>{formatClientAddr(detailShipment.clientAddr)}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card
              size="small"
              title={
                <span style={{ fontWeight: 600, fontSize: 15 }}>
                  入仓数据（{parseAgentMeasurementJson(detailShipment.agentMeasurementDataJson).rows.length}箱）
                </span>
              }
              extra={
                <Button
                  type="primary"
                  size="small"
                  loading={syncAgentDataFromDrawerLoading}
                  disabled={!detailShipment.agentInboundNo?.trim()}
                  onClick={syncAgentDataFromDrawer}
                >
                  同步代理入仓数据
                </Button>
              }
              style={{ marginBottom: 16 }}
            >
              <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
                <Descriptions.Item label="代理入库状态">
                  {detailShipment.agentInboundStatus != null
                    ? agentInboundStatusLabel[detailShipment.agentInboundStatus] ?? String(detailShipment.agentInboundStatus)
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="代理出库状态">
                  {detailShipment.agentOutboundStatus != null
                    ? agentOutboundStatusLabel[detailShipment.agentOutboundStatus] ?? String(detailShipment.agentOutboundStatus)
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="代理测量重量（毛重）">
                  {detailShipment.agentMeasuredWeightKg != null && detailShipment.agentMeasuredWeightKg > 0
                    ? `${detailShipment.agentMeasuredWeightKg} kg`
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="代理测量体积">
                  {detailShipment.agentMeasuredVolumeCbm != null && detailShipment.agentMeasuredVolumeCbm > 0
                    ? `${detailShipment.agentMeasuredVolumeCbm} m³`
                    : '-'}
                </Descriptions.Item>
              </Descriptions>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>代理测量数据</div>
              <AgentMeasurementTable json={detailShipment.agentMeasurementDataJson} />
            </Card>

            <Card
              size="small"
              title={<span style={{ fontWeight: 600, fontSize: 15 }}>轨迹数据</span>}
              extra={
                <Button
                  type="primary"
                  size="small"
                  loading={syncTrackingDataFromDrawerLoading}
                  disabled={!detailShipment.agentInboundNo?.trim() || detailShipment.status === 'DELIVERED'}
                  onClick={syncTrackingDataFromDrawer}
                >
                  同步轨迹
                </Button>
              }
              style={{ marginBottom: 16 }}
            >
              <TrackingDisplayPanel
                data={detailTrackingDisplay}
                fallbackEvents={detailShipment.agentTrackingLatest ?? undefined}
                loading={detailTrackingDisplayLoading}
              />
            </Card>

            <Card size="small" title={<span style={{ fontWeight: 600, fontSize: 15 }}>发票</span>} style={{ marginBottom: 16 }}>
              <Spin spinning={detailInvoicesLoading}>
              {detailInvoices.length === 0 ? (
                <div style={{ color: '#999' }}>暂无发票记录，可在列表操作中点击「生成发票」</div>
              ) : (
                <Table
                  dataSource={detailInvoices}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={[
                    { title: '文件名', dataIndex: 'fileName', ellipsis: true, render: (v: string) => v || '-' },
                    { title: '生成时间', dataIndex: 'generatedAt', width: 180, render: (v: string) => (v ? new Date(v).toLocaleString() : '-') },
                    {
                      title: '操作',
                      width: 80,
                      render: (_: unknown, inv: ShipmentInvoiceDTO) => (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (detailShipment?.shipmentId) onDownloadInvoice(detailShipment.shipmentId, inv);
                          }}
                        >
                          下载
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
              </Spin>
            </Card>

            <Card
              size="small"
              title={
                <span style={{ fontWeight: 600, fontSize: 15 }}>
                  绑定的货物（{detailCartonsLoading ? (detailShipment.cartonCount ?? 0) : detailCartons.length}箱）
                </span>
              }
              style={{ marginBottom: 16 }}
            >
              {detailShipment.cargoSyncPending && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="绑定箱数与代理测量箱数不一致"
                  description={
                    <div>
                      <p style={{ marginBottom: 8 }}>
                        运单已绑定 {detailShipment.cargoSyncKfyCount ?? detailCartons.length} 箱，代理测量{' '}
                        {detailShipment.cargoSyncAgentCount ?? '-'} 箱，请选择处理方式：
                      </p>
                      <Space wrap style={{ marginBottom: 8 }}>
                        <Button
                          type="primary"
                          danger
                          loading={resolveCargoSyncLoading}
                          onClick={() => {
                            Modal.confirm({
                              title: '确认替换？',
                              content: '将解绑当前运单下全部货物，再按代理测量清单新建或复用箱号并绑定。',
                              okText: '替换',
                              okButtonProps: { danger: true },
                              onOk: () => resolveCargoSync('REPLACE'),
                            });
                          }}
                        >
                          替换
                        </Button>
                        <Button
                          type="primary"
                          loading={resolveCargoSyncLoading}
                          onClick={() => {
                            Modal.confirm({
                              title: '确认合并？',
                              content: '保留已绑定货物，仅将代理侧尚不存在的箱号新建并绑定到本运单。',
                              okText: '合并',
                              onOk: () => resolveCargoSync('MERGE'),
                            });
                          }}
                        >
                          合并
                        </Button>
                        <Button
                          loading={resolveCargoSyncLoading}
                          onClick={() => resolveCargoSync('LATER')}
                        >
                          稍后手动处理
                        </Button>
                      </Space>
                      {detailShipment.agentMeasurementDataJson && (
                        <Collapse
                          size="small"
                          items={[
                            {
                              key: 'agent-cargo',
                              label: '查看代理测量箱清单',
                              children: (
                                <AgentMeasurementTable json={detailShipment.agentMeasurementDataJson} />
                              ),
                            },
                          ]}
                        />
                      )}
                    </div>
                  }
                />
              )}
              <Spin spinning={detailCartonsLoading}>
              <Table
                dataSource={detailCartons}
                rowKey="id"
                size="small"
                pagination={false}
                onRow={(record) => ({
                  onClick: () => {
                    if (record.id) openCartonInDetail(record.id);
                  },
                  style: { cursor: 'pointer' },
                })}
                columns={[
                  { title: '货物标识', dataIndex: 'cartonId', width: 160, ellipsis: true },
                  { title: '客户', dataIndex: 'belongClientId', width: 120, render: (v: string) => customers.find((c) => c.id === v || c.customerCode === v)?.name || v || '-' },
                  { title: '状态', dataIndex: 'state', width: 100, render: (v: string) => cartonStateLabel[v || ''] || v || '-' },
                ]}
              />
              </Spin>
            </Card>
          </>
        ) : null}
      </ResponsiveDrawer>
    </>
  );
};

export default ShipmentList;
